import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../services/api';
import { useGanttFlat } from './useGanttFlat';

export function useModularUnits() {
  const { projectId } = useParams();
  const { raw: ganttFlatData } = useGanttFlat();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/projects/${projectId}/modular-units`);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] response:', res.data);
      }
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.result)
        ? payload.result
        : Array.isArray(payload?.modularUnits)
        ? payload.modularUnits
        : Array.isArray(payload?.data?.modularUnits)
        ? payload.data.modularUnits
        : [];
      setUnits(list);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const createUnit = useCallback(async (unitData) => {
    try {
      const res = await api.post(`/projects/${projectId}/modular-units`, unitData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] create response:', res.data);
      }
      // Refresh the list after creating
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [projectId, fetchUnits]);

  const updateUnit = useCallback(async (modularUnitId, unitData) => {
    try {
      const res = await api.put(`/modular-units/${modularUnitId}`, unitData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] update response:', res.data);
      }
      // Refresh the list after updating
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  const checkUnitCanBeDeleted = useCallback((modularUnitId) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] checking gantt flat data:', ganttFlatData);
      }
      
      // Check if any data has parent matching modular_{modularUnitId}
      const parentToCheck = `modular_${modularUnitId}`;
      const hasChildData = ganttFlatData.some(item => item.parent === parentToCheck);
      
      if (hasChildData) {
        return { canDelete: false, reason: 'HAS_CATEGORIES_AND_TASKS' };
      }
      
      return { canDelete: true };
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useModularUnits] Error checking gantt flat data:', e);
      }
      // If error checking, assume it can be deleted
      return { canDelete: true };
    }
  }, [ganttFlatData]);

  const deleteUnit = useCallback(async (modularUnitId) => {
    try {
      const res = await api.delete(`/modular-units/${modularUnitId}`);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] delete response:', res.data);
      }
      // Refresh the list after deleting
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  const createCategory = useCallback(async (modularUnitId, categoryData) => {
    try {
      const res = await api.post(`/modular-units/${modularUnitId}/work-categories`, categoryData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] create category response:', res.data);
      }
      // Refresh the list after creating category
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  const updateCategory = useCallback(async (categoryId, categoryData) => {
    try {
      const res = await api.put(`/work-categories/${categoryId}`, categoryData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] update category response:', res.data);
      }
      // Refresh the list after updating category
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  const checkCategoryCanBeDeleted = useCallback((categoryId) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] checking category gantt flat data:', ganttFlatData);
      }
      
      // Check if any data has parent matching category_{categoryId}
      const parentToCheck = `category_${categoryId}`;
      const hasChildData = ganttFlatData.some(item => item.parent === parentToCheck);
      
      if (hasChildData) {
        return { canDelete: false, reason: 'HAS_TASKS' };
      }
      
      return { canDelete: true };
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useModularUnits] Error checking category gantt flat data:', e);
      }
      // If error checking, assume it can be deleted
      return { canDelete: true };
    }
  }, [ganttFlatData]);

  const deleteCategory = useCallback(async (categoryId) => {
    try {
      const res = await api.delete(`/work-categories/${categoryId}`);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] delete category response:', res.data);
      }
      // Refresh the list after deleting category
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  const createTask = useCallback(async (workCategoryId, taskData) => {
    try {
      const res = await api.post(`/work-categories/${workCategoryId}/tasks`, taskData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[useModularUnits] create task response:', res.data);
      }
      // Refresh the list after creating task
      await fetchUnits();
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [fetchUnits]);

  return { units, loading, error, refresh: fetchUnits, createUnit, updateUnit, deleteUnit, checkUnitCanBeDeleted, createCategory, updateCategory, checkCategoryCanBeDeleted, deleteCategory, createTask };
}
